import { env } from '../env';
import { orchestratorAuthHeader } from './orchestratorAuth';

export interface ProvisionResult {
  ok: boolean;
  runId: string;
  events?: Array<{
    step: string;
    status: string;
    message?: string;
    data?: Record<string, unknown>;
  }>;
}

export interface ProvisionStreamEvent {
  runId: string;
  step: string;
  status: string;
  message?: string;
  data?: Record<string, unknown>;
}

/**
 * Calls `POST /leads/:id/provision` on the orchestrator.
 * Uses the same bearer secret as `ORCHESTRATOR_BEARER_TOKEN` via
 * `VITE_ORCHESTRATOR_BEARER_TOKEN` (required for dashboard-triggered runs in dev).
 */
function formatProvisionError(
  status: number,
  statusText: string,
  body: unknown,
  rawText: string,
): string {
  if (body && typeof body === 'object') {
    const o = body as Record<string, unknown>;
    const reason = typeof o.reason === 'string' ? o.reason : undefined;
    const message = typeof o.message === 'string' ? o.message : undefined;
    const error = typeof o.error === 'string' ? o.error : undefined;
    const detail = reason ?? message ?? error;
    if (detail) {
      return `Provision failed (${status}): ${detail}`;
    }
  }
  return `Provision failed (${status}): ${rawText || statusText}`;
}

function parseSseBlock(block: string): { event?: string; dataLines: string[] } {
  let sseEvent: string | undefined;
  const dataLines: string[] = [];
  for (const rawLine of block.split(/\r?\n/)) {
    const line = rawLine.replace(/\r$/, '');
    if (line.startsWith('event:')) sseEvent = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
  }
  return { event: sseEvent, dataLines };
}

function dispatchProvisionSseBlock(block: string, onEvent: (ev: ProvisionStreamEvent) => void): void {
  if (!block.trim()) return;

  const { dataLines } = parseSseBlock(block);
  const payload = dataLines.join('\n').trim();
  if (!payload) return;

  let parsed: unknown;
  try {
    parsed = JSON.parse(payload);
  } catch {
    return;
  }

  if (!parsed || typeof parsed !== 'object') return;
  const o = parsed as Record<string, unknown>;
  const runId = typeof o.runId === 'string' ? o.runId : '';
  const step = typeof o.step === 'string' ? o.step : '';
  const status = typeof o.status === 'string' ? o.status : '';
  const message = typeof o.message === 'string' ? o.message : undefined;
  const data = o.data;
  const dataObj =
    data && typeof data === 'object' && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : undefined;

  if (!step || !status) return;

  const ev: ProvisionStreamEvent = { runId, step, status, message, data: dataObj };
  onEvent(ev);

  if (status === 'error') {
    throw new Error(message ?? 'Provisioning failed');
  }
}

/**
 * `POST /leads/:id/provision?stream=true` — Server-Sent Events with one message per pipeline tick.
 * Invokes `onEvent` for each step; throws if the pipeline reports `status: 'error'`.
 */
export async function provisionLeadStream(
  leadId: string,
  onEvent: (ev: ProvisionStreamEvent) => void,
): Promise<void> {
  const authorization = await orchestratorAuthHeader();
  const url = `${env.orchestratorUrl}/leads/${encodeURIComponent(leadId)}/provision?stream=true`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization,
      },
    });
  } catch (e) {
    const hint =
      e instanceof TypeError
        ? ` Cannot reach orchestrator at ${url}. Start it with pnpm --filter orchestrator dev (or check VITE_ORCHESTRATOR_URL).`
        : '';
    throw new Error(e instanceof Error ? `${e.message}.${hint}` : String(e) + hint);
  }

  if (!res.ok) {
    const text = await res.text();
    let body: unknown;
    try {
      body = text ? JSON.parse(text) : {};
    } catch {
      body = { raw: text };
    }
    throw new Error(formatProvisionError(res.status, res.statusText, body, text));
  }

  const reader = res.body?.getReader();
  if (!reader) {
    throw new Error('Provision stream: empty response body');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split(/\r?\n\r?\n/);
      buffer = parts.pop() ?? '';

      for (const block of parts) {
        dispatchProvisionSseBlock(block, onEvent);
      }
    }

    buffer += decoder.decode();
    const tailParts = buffer.split(/\r?\n\r?\n/);
    for (const block of tailParts) {
      dispatchProvisionSseBlock(block, onEvent);
    }
  } finally {
    reader.releaseLock();
  }
}

export async function provisionLead(leadId: string): Promise<ProvisionResult> {
  const events: NonNullable<ProvisionResult['events']> = [];
  let runId = '';

  await provisionLeadStream(leadId, (ev) => {
    if (ev.runId) runId = ev.runId;
    events.push({
      step: ev.step,
      status: ev.status,
      message: ev.message,
      data: ev.data,
    });
  });

  const ok = !events.some((e) => e.status === 'error');
  return { ok, runId, events };
}

/**
 * Promote a shared/demo lead to an isolated client.
 *
 * Calls `POST /leads/:id/promote` on the orchestrator, which flips the lead's
 * `provisioning.mode` to `'isolated'`, drops the prior `isolation_handshake`
 * step, and resets `status` to `'idle'`. The dashboard should immediately
 * trigger a new provisioning run after this returns.
 */
export async function promoteLead(leadId: string): Promise<void> {
  const authorization = await orchestratorAuthHeader();
  const url = `${env.orchestratorUrl}/leads/${encodeURIComponent(leadId)}/promote`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization,
      },
    });
  } catch (e) {
    const hint =
      e instanceof TypeError
        ? ` Cannot reach orchestrator at ${url}. Start it with pnpm --filter orchestrator dev (or check VITE_ORCHESTRATOR_URL).`
        : '';
    throw new Error(e instanceof Error ? `${e.message}.${hint}` : String(e) + hint);
  }

  if (!res.ok) {
    const text = await res.text();
    let body: unknown;
    try {
      body = text ? JSON.parse(text) : {};
    } catch {
      body = { raw: text };
    }
    throw new Error(formatProvisionError(res.status, res.statusText, body, text));
  }
}

/** One-way lock: Flashpoint will not push to this lead's GitHub repo again. */
export async function lockLeadCode(leadId: string): Promise<void> {
  const authorization = await orchestratorAuthHeader();
  const url = `${env.orchestratorUrl}/leads/${encodeURIComponent(leadId)}/lock-code`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization },
    });
  } catch (e) {
    const hint =
      e instanceof TypeError
        ? ` Cannot reach orchestrator at ${url}. Start it with pnpm --filter orchestrator dev.`
        : '';
    throw new Error(e instanceof Error ? `${e.message}.${hint}` : String(e) + hint);
  }
  if (!res.ok) {
    const text = await res.text();
    let body: unknown;
    try {
      body = text ? JSON.parse(text) : {};
    } catch {
      body = { raw: text };
    }
    throw new Error(formatProvisionError(res.status, res.statusText, body, text));
  }
}

export interface PipelineLead {
  id: string;
  label: string;
  personName: string;
  businessName: string;
  phone: string;
  email: string;
  status: string;
}

export interface PipelineLeadList {
  items: PipelineLead[];
  warning?: string;
}

/** `GET /pipeline/leads` — Logic Brooks CRM rows for the New preview picker. Never throws. */
export async function fetchPipelineLeads(): Promise<PipelineLeadList> {
  try {
    const authorization = await orchestratorAuthHeader();
    const url = `${env.orchestratorUrl}/pipeline/leads`;
    const res = await fetch(url, { headers: { authorization } });
    if (!res.ok) {
      return { items: [], warning: 'Pipeline list unavailable — type a business name to continue.' };
    }
    const body: unknown = await res.json();
    if (!body || typeof body !== 'object') {
      return { items: [], warning: 'Pipeline list unavailable — type a business name to continue.' };
    }
    const o = body as Record<string, unknown>;
    const rawItems = Array.isArray(o.items) ? o.items : [];
    const items: PipelineLead[] = [];
    for (const row of rawItems) {
      if (!row || typeof row !== 'object') continue;
      const r = row as Record<string, unknown>;
      if (typeof r.id !== 'string' || typeof r.label !== 'string') continue;
      items.push({
        id: r.id,
        label: r.label,
        personName: typeof r.personName === 'string' ? r.personName : '',
        businessName: typeof r.businessName === 'string' ? r.businessName : '',
        phone: typeof r.phone === 'string' ? r.phone : '',
        email: typeof r.email === 'string' ? r.email : '',
        status: typeof r.status === 'string' ? r.status : '',
      });
    }
    const warning = typeof o.warning === 'string' ? o.warning : undefined;
    return { items, warning };
  } catch {
    return { items: [], warning: 'Pipeline list unavailable — type a business name to continue.' };
  }
}
